import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import useCategories from "./hooks/useCategories";
import { EmptyState } from "../../../../core/ui";
import { CategoriesService } from "../../../../core/http/services/CategoriesService";

const TYPE_OPTIONS = [
  { value: "MASC", label: "Masculino" },
  { value: "FEM", label: "Feminino" },
  { value: "MISTA", label: "Mista" },
];

const emptyCategoryForm = { name: "", type: "" };

const Categories = () => {
  const { categories, setCategories, fetchCategories } = useCategories();

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const uniqueTypes = useMemo(
    () => [...new Set(categories.map((c) => c.type))],
    [categories],
  );

  const [selectedTab, setSelectedTab] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState(emptyCategoryForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (uniqueTypes.length > 0 && !selectedTab) {
      setSelectedTab(uniqueTypes[0]);
    }
  }, [uniqueTypes, selectedTab]);

  const filteredCategories = categories.filter((c) => c.type === selectedTab);

  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const { status } = await CategoriesService.delete(deleteTarget.id);
    setDeleteLoading(false);
    setDeleteTarget(null);

    if (status === 204) {
      setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setSnackbar({ open: true, message: "Categoria removida com sucesso.", severity: "success" });
    } else if (status === 409) {
      setSnackbar({ open: true, message: "Não é possível excluir: categoria possui jogadores ou grupos vinculados.", severity: "warning" });
    } else if (status === 404) {
      setSnackbar({ open: true, message: "Categoria não encontrada.", severity: "error" });
    } else {
      setSnackbar({ open: true, message: "Erro ao excluir a categoria.", severity: "error" });
    }
  };

  const handleAddCategory = async () => {
    const { status } = await CategoriesService.create({
      name: newCategoryForm.name.trim() || "Nova categoria",
      level: 4,
      type: newCategoryForm.type || uniqueTypes[0] || "MASC",
    });

    if (status === 200 || status === 201) {
      await fetchCategories();
    }

    setNewCategoryForm(emptyCategoryForm);
    setAddModalOpen(false);
  };

  const handleFormChange = (field) => (event) => {
    setNewCategoryForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Categorias
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Adicionar categoria
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 1.2,
        }}
      >
        <Tabs
          value={selectedTab || false}
          onChange={(_, value) => setSelectedTab(value)}
          sx={{ mb: 1.2 }}
        >
          {TYPE_OPTIONS.map((type) => (
            <Tab key={type.value} value={type.value} label={type.label} />
          ))}
        </Tabs>

        <Stack spacing={1}>
          {filteredCategories.length === 0 ? (
            <EmptyState
              icon={<CategoryOutlinedIcon sx={{ fontSize: 34 }} />}
              title="Nenhuma categoria cadastrada"
              description="Nenhuma categoria cadastrada neste tipo."
              sx={{ py: 3 }}
            />
          ) : (
            filteredCategories.map((category) => (
              <Paper
                key={category.id}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1.1,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    {category.name}
                  </Typography>

                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleteTarget(category)}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Excluir
                  </Button>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Excluir categoria</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a categoria{" "}
            <strong>{deleteTarget?.name}</strong>? Esta ação não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCategory}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Adicionar categoria</DialogTitle>
        <DialogContent>
          <Stack spacing={1.3} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              label="Nome da categoria"
              value={newCategoryForm.name}
              onChange={handleFormChange("name")}
            />

            <FormControl fullWidth>
              <InputLabel id="category-type-label">Tipo</InputLabel>
              <Select
                labelId="category-type-label"
                value={newCategoryForm.type}
                label="Tipo"
                onChange={handleFormChange("type")}
              >
                {TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddCategory}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Categories;
